import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import Spinner from '../../components/ui/Spinner';
import { getDocument, signatureKeys } from './signaturesApi';
import StepFields from './components/StepFields';
import StepReview from './components/StepReview';
import StepUpload from './components/StepUpload';

const STEPS = ['Document & recipients', 'Place fields', 'Review & publish'];

/**
 * The 3-step builder wizard (OtterWaiver's agreement creator). `/signatures/new`
 * starts a document; after step 1 it becomes `/signatures/:id/edit?step=2`, so
 * a draft can be resumed at any point.
 */
export default function DocumentWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = searchParams.get('type') === 'waiver' ? 'waiver' : 'agreement';
  const step = Math.min(3, Math.max(1, Number(searchParams.get('step') ?? 1)));

  const detail = useQuery({
    queryKey: signatureKeys.detail(id),
    queryFn: () => getDocument(id),
    enabled: Boolean(id),
  });

  if (id && detail.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const doc = detail.data?.document ?? null;
  const effectiveType = doc?.type ?? type;

  // Published/completed documents are no longer editable — show the detail page.
  if (doc && doc.status !== 'draft') {
    return <Navigate to={`/signatures/${doc.id}`} replace />;
  }

  const goToStep = async (docId, nextStep) => {
    // Await the refetch so the next step mounts against fresh detail data
    // (pagesInPdf, fields) instead of the pre-save cache.
    await queryClient.invalidateQueries({ queryKey: signatureKeys.detail(docId) });
    navigate(`/signatures/${docId}/edit?step=${nextStep}`, { replace: !id });
  };

  return (
    <div className="space-y-6">
      {/* Stepper header */}
      <div className="flex items-center justify-center gap-2">
        {STEPS.map((label, index) => {
          const number = index + 1;
          const reachable = doc ? number <= 3 : number === 1;
          const state = number < step ? 'done' : number === step ? 'current' : 'todo';
          return (
            <button
              key={label}
              type="button"
              disabled={!reachable}
              onClick={() => doc && setSearchParams({ step: String(number) })}
              className="flex items-center gap-2 disabled:cursor-default"
            >
              <span
                className={clsx(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                  state === 'done' && 'bg-brand-600 text-white',
                  state === 'current' && 'bg-slate-900 text-slate-50',
                  state === 'todo' && 'bg-slate-200 text-slate-500',
                )}
              >
                {state === 'done' ? <Check className="h-4 w-4" /> : number}
              </span>
              <span
                className={clsx(
                  'hidden text-sm font-medium sm:block',
                  state === 'current' ? 'text-slate-900' : 'text-slate-400',
                )}
              >
                {label}
              </span>
              {number < STEPS.length && <span className="mx-1 h-px w-8 bg-slate-300" />}
            </button>
          );
        })}
      </div>

      {step === 1 && (
        <StepUpload
          type={effectiveType}
          document={doc}
          signers={detail.data?.signers?.filter((s) => s.kind === 'named')}
          onDone={(document) => goToStep(document.id, 2)}
        />
      )}
      {step === 2 && doc && (
        <StepFields
          document={doc}
          signers={detail.data.signers.filter((s) => s.kind === 'named')}
          initialFields={detail.data.fields}
          onDone={() => goToStep(doc.id, 3)}
        />
      )}
      {step === 3 && doc && (
        <StepReview
          document={doc}
          signers={detail.data.signers.filter((s) => s.kind === 'named')}
          fields={detail.data.fields}
          onPublished={(document) => {
            queryClient.invalidateQueries({ queryKey: signatureKeys.detail(document.id) });
            queryClient.invalidateQueries({ queryKey: ['signatures'] });
            navigate(`/signatures/${document.id}`);
          }}
        />
      )}
    </div>
  );
}
